import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'

import InfoBox from 'components/Shared/InfoBox'
import { SetupPlatform } from 'components/Recommendations/Campaign/platform'
import SelectItem from 'components/Shared/SelectItem'
import useHasPermission, { Permissions } from 'hooks/useHasPermission'
import { fetchPartnerBranding } from 'api/partner'

const ALL_PLATFORM_OPTIONS = [SetupPlatform.meta, SetupPlatform.google, SetupPlatform.both]

const Platforms = ({ selectedPlatform, onPlatformSelected, disabled = false }) => {
  const { t } = useTranslation()
  const hasGoogleAccess = useHasPermission(Permissions.google)
  const platformOptions = hasGoogleAccess
    ? ALL_PLATFORM_OPTIONS
    : [SetupPlatform.meta]

  const [isGoogleBrandingMissing, setIsGoogleBrandingMissing] = useState(false)

  useEffect(() => {
    if (!hasGoogleAccess) return undefined
    let cancelled = false;
    (async () => {
      try {
        const { businessName, logoUrl } = await fetchPartnerBranding()
        if (!cancelled) setIsGoogleBrandingMissing(!businessName || !logoUrl)
      } catch (error) {
        // Leave Google options enabled if the branding check itself fails.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [hasGoogleAccess])

  return (
    <div className="d-flex flex-column h-100">
      <InfoBox
        title={t('Tips.Recommendations.SelectPlatform.title')}
        body={t('Tips.Recommendations.SelectPlatform.body')}
        placement="bottom"
      >
        <h2 className="d-inline-block section-title">
          {t('Recommendations.platform.selectLabel')}
        </h2>
      </InfoBox>
      <div
        className={`d-flex flex-column gap-3 event-header-card h-100 campaign-platforms ${disabled ? 'disabled' : ''}`}
      >
        {platformOptions.map((platform) => {
          const optionLabel = t(`Recommendations.platform.options.${platform}`)
          const requiresGoogleBranding = isGoogleBrandingMissing && platform !== SetupPlatform.meta
          return (
            <InfoBox
              key={platform}
              enabled={requiresGoogleBranding}
              withAnchor={false}
              placement="top"
              body={
                <Trans
                  i18nKey="Recommendations.platform.googleBrandingRequired"
                  components={{
                    a: (
                      <a
                        href="/settings?platform=google#platforms"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline', display: 'inline-block' }}
                      >
                        settings
                      </a>
                    ),
                  }}
                />
              }
            >
              <SelectItem
                checked={platform === selectedPlatform}
                onChange={() => onPlatformSelected(platform)}
                disabled={disabled || requiresGoogleBranding}
                title={optionLabel}
              />
            </InfoBox>
          )
        })}
      </div>
    </div>
  )
}

Platforms.propTypes = {
  selectedPlatform: PropTypes.oneOf(Object.values(SetupPlatform)).isRequired,
  onPlatformSelected: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

export default Platforms
